import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Send, Mail, Clock, CheckCircle2, Eye } from "lucide-react";
import DOMPurify from "dompurify";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  adminCreateNewsletter,
  adminGetNewsletter,
  adminUpdateNewsletter,
  adminSendTest,
  adminSendNewsletter,
  adminScheduleNewsletter,
  adminListSubscribers,
} from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY = { subject: "", from_subtitle: "", content: "" };

const FORMAT_HINT = `## Big heading
### Small heading
- bullet item
1. numbered item
**bold text**
*italic text*
[link text](https://example.com)
(leave a blank line between paragraphs)`;

function renderPreview(md) {
  if (!md) return "";
  const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) =>
    escape(s)
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-ink">$1</strong>')
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" class="text-forest underline">$1</a>');
  return md
    .split(/\n{2,}/)
    .map((b) => {
      if (b.startsWith("### ")) return `<h3 class="font-display font-bold text-lg mt-6 mb-2 text-ink">${inline(b.slice(4))}</h3>`;
      if (b.startsWith("## ")) return `<h2 class="font-display font-bold text-2xl mt-7 mb-2 text-ink">${inline(b.slice(3))}</h2>`;
      if (/^(- |\* )/m.test(b)) {
        const items = b.split("\n").filter(Boolean).map((l) => `<li>${inline(l.replace(/^(- |\* )/, ""))}</li>`).join("");
        return `<ul class="list-disc pl-5 my-3 text-ink">${items}</ul>`;
      }
      if (/^\d+\.\s+/m.test(b)) {
        const items = b.split("\n").filter(Boolean).map((l) => `<li>${inline(l.replace(/^\d+\.\s+/, ""))}</li>`).join("");
        return `<ol class="list-decimal pl-5 my-3 text-ink">${items}</ol>`;
      }
      return `<p class="my-3 text-ink leading-relaxed">${inline(b).replace(/\n/g, "<br/>")}</p>`;
    })
    .join("\n");
}

export default function AdminNewsletterEditor() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("draft");
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);
  const [subs, setSubs] = useState({ active: 0, total: 0 });
  const [showSend, setShowSend] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleValue, setScheduleValue] = useState("");

  useEffect(() => {
    adminListSubscribers().then((s) => setSubs({ active: s.active, total: s.total })).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    adminGetNewsletter(id)
      .then((n) => {
        setForm({ subject: n.subject, from_subtitle: n.from_subtitle || "", content: n.content });
        setStatus(n.status);
      })
      .catch(() => toast.error("Couldn't load newsletter."))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const locked = status === "sent" || status === "sending";

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const save = async (silent = false) => {
    if (!form.subject || !form.content) {
      toast.error("Subject and content are required.");
      return null;
    }
    setBusy(true);
    try {
      const payload = {
        subject: form.subject,
        from_subtitle: form.from_subtitle || null,
        content: form.content,
      };
      const saved = isEdit ? await adminUpdateNewsletter(id, payload) : await adminCreateNewsletter(payload);
      if (!silent) toast.success("Saved.");
      if (!isEdit) navigate(`/admin/newsletter/edit/${saved.id}`, { replace: true });
      return saved;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const onSendTest = async () => {
    const saved = await save(true);
    const newsletterId = saved?.id || id;
    if (!newsletterId) return;
    try {
      await adminSendTest(newsletterId);
      toast.success("Test email sent to you 💜");
    } catch {
      toast.error("Test send failed.");
    }
  };

  const onConfirmSend = async () => {
    const saved = await save(true);
    const newsletterId = saved?.id || id;
    if (!newsletterId) return;
    try {
      await adminSendNewsletter(newsletterId);
      toast.success(`Sending to ${subs.active} subscribers. Check status on the list page.`);
      setShowSend(false);
      navigate("/admin/newsletter");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Send failed.");
    }
  };

  const onConfirmSchedule = async () => {
    if (!scheduleValue) {
      toast.error("Pick a date and time.");
      return;
    }
    const saved = await save(true);
    const newsletterId = saved?.id || id;
    if (!newsletterId) return;
    try {
      const iso = new Date(scheduleValue).toISOString();
      await adminScheduleNewsletter(newsletterId, iso);
      toast.success("Scheduled.");
      setShowSchedule(false);
      navigate("/admin/newsletter");
    } catch {
      toast.error("Schedule failed.");
    }
  };

  if (loading) return <div className="text-ink-muted text-sm" data-testid="newsletter-editor-loading">Loading…</div>;

  return (
    <div data-testid="admin-newsletter-editor-page">
      <Link
        to="/admin/newsletter"
        data-testid="newsletter-editor-back"
        className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-forest link-underline mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back to newsletters
      </Link>

      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="overline text-terracotta">{isEdit ? (locked ? "Newsletter" : "Edit newsletter") : "New newsletter"}</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-ink mt-2">
            {form.subject || "Untitled newsletter"}
          </h1>
          {locked && (
            <p className="text-sm text-ink-muted mt-2 inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-forest" /> {status === "sent" ? "This newsletter has been sent and is read-only." : "Currently sending…"}
            </p>
          )}
        </div>
        <div className="text-sm text-ink-muted text-right">
          <p className="font-semibold text-ink">{subs.active} active subscribers</p>
          <p>{subs.total - subs.active} unsubscribed</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* FORM */}
        <div className="lg:col-span-3 space-y-5">
          <div className="bg-paper-100 border border-paper-200 p-6 rounded-sm space-y-5">
            <div>
              <Label htmlFor="subject" className="text-ink font-semibold">Email subject *</Label>
              <Input
                id="subject"
                value={form.subject}
                onChange={upd("subject")}
                disabled={locked}
                data-testid="newsletter-subject"
                className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2"
                placeholder="What's inside this issue?"
              />
            </div>
            <div>
              <Label htmlFor="from_subtitle" className="text-ink font-semibold">
                "From" subtitle
              </Label>
              <Input
                id="from_subtitle"
                value={form.from_subtitle}
                onChange={upd("from_subtitle")}
                disabled={locked}
                data-testid="newsletter-subtitle"
                className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2"
                placeholder="October Update"
              />
              <p className="text-xs text-ink-muted mt-1.5">
                Shows as "The STEM Vet — {form.from_subtitle || "Your subtitle"}" in the recipient's inbox.
              </p>
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <Label htmlFor="content" className="text-ink font-semibold">Content *</Label>
                <span className="text-xs text-ink-muted">Markdown supported</span>
              </div>
              <Textarea
                id="content"
                value={form.content}
                onChange={upd("content")}
                disabled={locked}
                data-testid="newsletter-content"
                rows={18}
                className="mt-2 bg-paper-50 border-paper-200 focus-visible:ring-forest focus-visible:ring-2 font-mono text-sm leading-relaxed"
                placeholder="Hey there,\n\nWrite your newsletter here..."
              />
              <pre className="mt-3 text-[11px] text-ink-muted bg-paper-50 border border-paper-200 rounded-sm p-3 whitespace-pre-wrap">
{FORMAT_HINT}
              </pre>
            </div>
          </div>

          {!locked && (
            <div className="bg-paper-100 border border-paper-200 p-6 rounded-sm space-y-3">
              <p className="overline text-forest">Actions</p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => save(false)}
                  disabled={busy}
                  data-testid="newsletter-save-draft"
                  className="inline-flex items-center gap-2 bg-ink text-paper-50 px-5 py-3 text-sm font-semibold rounded-sm hover:bg-ink/90 transition-colors disabled:opacity-60"
                >
                  <Save className="w-4 h-4" /> Save draft
                </button>
                <button
                  type="button"
                  onClick={onSendTest}
                  disabled={busy}
                  data-testid="newsletter-send-test"
                  className="inline-flex items-center gap-2 bg-paper-50 border border-paper-200 text-ink px-5 py-3 text-sm font-semibold rounded-sm hover:bg-paper-200 transition-colors disabled:opacity-60"
                >
                  <Mail className="w-4 h-4" /> Send test to me
                </button>
                <button
                  type="button"
                  onClick={() => setShowSchedule(true)}
                  disabled={busy}
                  data-testid="newsletter-schedule"
                  className="inline-flex items-center gap-2 bg-scholar text-ink px-5 py-3 text-sm font-semibold rounded-sm hover:bg-scholar-dark transition-colors disabled:opacity-60"
                >
                  <Clock className="w-4 h-4" /> Schedule
                </button>
                <button
                  type="button"
                  onClick={() => setShowSend(true)}
                  disabled={busy}
                  data-testid="newsletter-send-now"
                  className="inline-flex items-center gap-2 bg-forest text-paper-50 px-5 py-3 text-sm font-semibold rounded-sm hover:bg-forest-700 transition-colors disabled:opacity-60"
                >
                  <Send className="w-4 h-4" /> Send now
                </button>
              </div>
            </div>
          )}
        </div>

        {/* PREVIEW */}
        <aside className="lg:col-span-2 space-y-3">
          <div className="bg-paper-100 border border-paper-200 p-5 rounded-sm sticky top-24">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="w-4 h-4 text-forest" />
              <p className="overline text-terracotta">Preview</p>
            </div>
            <div className="bg-paper-50 border border-paper-200 rounded-sm overflow-hidden">
              <div className="bg-forest text-paper-50 px-5 py-4">
                <p className="text-[11px] uppercase tracking-widest opacity-80">
                  The STEM Vet · {form.from_subtitle || "Newsletter"}
                </p>
                <p className="font-display font-bold text-lg leading-snug mt-1">
                  {form.subject || "(subject)"}
                </p>
              </div>
              <div
                className="p-5 max-h-[520px] overflow-y-auto text-sm"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderPreview(form.content)) }}
                data-testid="newsletter-preview"
              />
              <div className="bg-paper-100 border-t border-paper-200 px-5 py-3 text-[11px] text-ink-muted text-center">
                You're receiving this because you subscribed. Unsubscribe here.
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* Send confirmation */}
      <AlertDialog open={showSend} onOpenChange={setShowSend}>
        <AlertDialogContent data-testid="newsletter-send-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send to {subs.active} subscribers?</AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately email "{form.subject}" to every active subscriber. Once sent, you can't edit or delete it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="newsletter-send-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmSend}
              data-testid="newsletter-send-confirm"
              className="bg-forest hover:bg-forest-700 text-paper-50"
            >
              Yes, send now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Schedule dialog */}
      <AlertDialog open={showSchedule} onOpenChange={setShowSchedule}>
        <AlertDialogContent data-testid="newsletter-schedule-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Schedule send</AlertDialogTitle>
            <AlertDialogDescription>
              Pick when to deliver this newsletter to {subs.active} subscribers. Times are in your local timezone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-3">
            <Input
              type="datetime-local"
              value={scheduleValue}
              onChange={(e) => setScheduleValue(e.target.value)}
              data-testid="newsletter-schedule-input"
              className="bg-paper-50 border-paper-200"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="newsletter-schedule-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onConfirmSchedule}
              data-testid="newsletter-schedule-confirm"
              className="bg-scholar hover:bg-scholar-dark text-ink"
            >
              Schedule
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
